'use client';

import {
  CommandEmpty,
  CommandGroup,
  CommandItem
} from '@/components/ui/command';
import { Folder, Hash, User, Loader2 } from 'lucide-react';
import {
  useSearch,
  MatterSearchResult
} from '@/components/search/hooks/use-search';
import { useContactSearch } from '@/components/search/hooks/use-contact-search';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { getContactDisplayName } from '@/lib/services/contacts';
import { MatterProgress } from '@/components/ui/things/list-card/matter-progress';
import { useFeatureFlags } from '@/lib/providers/feature-flags-provider';
import { SearchResultsViewProps } from './types';

/**
 * SearchResultsView Component
 *
 * Displays search results for matters and contacts.
 * Both searches run independently and results appear in separate sections.
 */
export function SearchResultsView({
  query,
  onSelect,
  onContactSelect
}: SearchResultsViewProps) {
  const { hasFeature } = useFeatureFlags();
  const contactsFeatureEnabled = hasFeature('contacts-page');

  const { matters, isLoading: mattersLoading } = useSearch(query);
  const { contacts, isLoading: contactsLoading } = useContactSearch(
    query,
    contactsFeatureEnabled
  );

  // Mirror the 300ms debounce the data hooks use (both hardcode 300) so we can
  // tell "still debouncing" apart from "settled with no results". Without this
  // the no-results message flashes during the debounce window before the query
  // fires — and with the now fixed dialog height that blank flash is obvious.
  const debouncedQuery = useDebouncedValue(query, 300);
  const isLoading =
    debouncedQuery !== query || mattersLoading || contactsLoading;

  // Show "type at least 2 characters" for single character
  if (query.length === 1) {
    return (
      <div className="text-muted-foreground py-6 text-center text-sm">
        Type at least 2 characters to search
      </div>
    );
  }

  // While debouncing or fetching, show a spinner rather than a blank region or
  // a premature "No results found." message.
  if (isLoading) {
    return (
      <div className="text-muted-foreground flex items-center justify-center gap-2 py-10 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Searching…
      </div>
    );
  }

  // Show empty state when no results from either source
  if (query.length >= 2 && matters.length === 0 && contacts.length === 0) {
    return <CommandEmpty>No results found.</CommandEmpty>;
  }

  return (
    <>
      {matters.length > 0 && (
        <CommandGroup heading="Matters">
          {matters.map((matter) => (
            <MatterResultItem
              key={matter.id}
              matter={matter}
              onSelect={onSelect}
            />
          ))}
        </CommandGroup>
      )}

      {contacts.length > 0 && onContactSelect && (
        <CommandGroup heading="Contacts">
          {contacts.map((contact) => (
            <CommandItem
              key={contact.id}
              value={`contact-${contact.id}-${getContactDisplayName(contact)}`}
              onSelect={() => onContactSelect(contact)}
              className="cursor-pointer"
            >
              <User className="mr-2 h-4 w-4" />
              <div className="flex flex-1 flex-col">
                <span className="font-medium">
                  {getContactDisplayName(contact)}
                </span>
                {contact.email && (
                  <span className="text-muted-foreground text-sm">
                    {contact.email}
                  </span>
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      )}
    </>
  );
}

export function MatterResultItem({
  matter,
  onSelect
}: {
  matter: MatterSearchResult;
  onSelect: (matter: MatterSearchResult) => void;
}) {
  const clients = matter.clients?.length ? matter.clients : null;
  const clientNames = clients
    ? clients.map(
        (c) =>
          c.organisationName ||
          [c.firstName, c.lastName].filter(Boolean).join(' ') ||
          `Client #${c.id}`
      )
    : matter.clientNames;

  return (
    <CommandItem
      key={matter.id}
      value={`${matter.isEstateAdmin ? matter.reference : matter.description} ${matter.resultTitle}`}
      onSelect={() => onSelect(matter)}
      className="cursor-pointer"
    >
      {/* Leading anchor icon — matches the matter list's Folder glyph (LF-4960) */}
      <Folder className="mr-2 h-4 w-4 shrink-0 text-slate-400" />
      <div className="flex flex-1 flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-2">
          <span className="truncate font-medium">
            {matter.isEstateAdmin
              ? matter.resultTitle || 'Unknown deceased'
              : matter.description}
          </span>
          {/* Progress dots replace the text status pill — matches the
              matter list's stage-completion indicator (LF-4960). Only
              estate-administration matters carry stage data. */}
          {!!matter.progress && (
            <MatterProgress
              completed={matter.progress.completed}
              total={matter.progress.total}
              stages={matter.progress.stages}
              compact
            />
          )}
        </div>
        <span className="text-muted-foreground flex flex-wrap items-center gap-1 text-sm">
          {/* Styled reference badge — matches the matter list treatment
              (LF-4960), replacing the plain-text reference. */}
          {!!matter.reference && (
            <span className="text-12px inline-flex shrink-0 items-center gap-1 rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-medium text-slate-500">
              <Hash className="!h-2.5 !w-2.5 text-slate-400" />
              {matter.reference}
            </span>
          )}
          {matter.isEstateAdmin && !!matter.lprNames ? (
            <span className="capitalize">• {matter.lprNames}</span>
          ) : null}
          {!matter.isEstateAdmin && !!matter.matterTypeLabel ? (
            <span className="capitalize">• {matter.matterTypeLabel}</span>
          ) : null}
          {!matter.isEstateAdmin && !matter.matterTypeLabel ? (
            !clientNames?.length ? (
              <span>• Unknown clients</span>
            ) : (
              <span>
                •{' '}
                {clientNames.map((name: string, idx: number) => (
                  <span
                    // eslint-disable-next-line @eslint-react/no-array-index-key -- client names have no stable id and can repeat (e.g. a couple sharing a surname); position is the identity
                    key={idx}
                    className="me-1 inline-block first-letter:uppercase"
                  >
                    {name}
                    {idx !== clientNames.length - 1 ? ',' : ''}
                  </span>
                ))}
              </span>
            )
          ) : null}
        </span>
      </div>
    </CommandItem>
  );
}
